/**
 * Định nghĩa schema dưới dạng DỮ LIỆU (không hardcode form) để dễ thêm loại mới.
 *
 * Mỗi field:
 *   - name      : tên property schema.org.
 *   - label     : nhãn hiển thị.
 *   - type      : 'text' | 'textarea' | 'url' | 'tel' | 'number' | 'date' | 'datetime'
 *                 | 'object' (1 entity con) | 'array' (danh sách entity con lặp lại).
 *   - required  : bắt buộc (validate cơ bản ở form).
 *   - rich      : "bắt buộc để có rich result" (đánh dấu nổi bật cho user).
 *   - of        : @type của entity con (cho 'object'/'array').
 *   - fields    : field con (cho 'object'/'array', có thể lồng tiếp).
 *
 * Ưu tiên các loại có rich result thật sự (features.md §2.1).
 */

const ADDRESS_FIELDS = [
	{name: 'streetAddress', label: 'Số nhà, đường', type: 'text'},
	{name: 'addressLocality', label: 'Phường/Xã/Quận', type: 'text'},
	{name: 'addressRegion', label: 'Tỉnh/Thành phố', type: 'text'},
	{name: 'postalCode', label: 'Mã bưu chính', type: 'text'},
	{name: 'addressCountry', label: 'Quốc gia (vd VN)', type: 'text'},
];

export const SCHEMA_TYPES = {
	Article: {
		label: 'Bài viết (Article)',
		fields: [
			{name: 'headline', label: 'Tiêu đề', type: 'text', required: true, rich: true},
			{name: 'image', label: 'URL ảnh', type: 'url', rich: true},
			{name: 'author', label: 'Tác giả', type: 'object', of: 'Person', rich: true, fields: [
				{name: 'name', label: 'Tên tác giả', type: 'text'},
				{name: 'url', label: 'Trang tác giả', type: 'url'},
			]},
			{name: 'publisher', label: 'Nhà xuất bản', type: 'object', of: 'Organization', rich: true, fields: [
				{name: 'name', label: 'Tên', type: 'text'},
				{name: 'logo', label: 'URL logo', type: 'url'},
			]},
			{name: 'datePublished', label: 'Ngày đăng', type: 'datetime', rich: true},
			{name: 'dateModified', label: 'Ngày cập nhật', type: 'datetime'},
			{name: 'description', label: 'Mô tả', type: 'textarea'},
		],
	},

	Product: {
		label: 'Sản phẩm (Product)',
		fields: [
			{name: 'name', label: 'Tên sản phẩm', type: 'text', required: true, rich: true},
			{name: 'image', label: 'URL ảnh', type: 'url', rich: true},
			{name: 'description', label: 'Mô tả', type: 'textarea'},
			{name: 'sku', label: 'SKU', type: 'text'},
			{name: 'brand', label: 'Thương hiệu', type: 'object', of: 'Brand', fields: [
				{name: 'name', label: 'Tên thương hiệu', type: 'text'},
			]},
			{name: 'offers', label: 'Chào bán (Offer)', type: 'object', of: 'Offer', rich: true, fields: [
				{name: 'price', label: 'Giá', type: 'number'},
				{name: 'priceCurrency', label: 'Tiền tệ (vd VND)', type: 'text'},
				{name: 'availability', label: 'Tình trạng (vd https://schema.org/InStock)', type: 'text'},
				{name: 'url', label: 'URL mua', type: 'url'},
			]},
			{name: 'aggregateRating', label: 'Đánh giá tổng hợp', type: 'object', of: 'AggregateRating', fields: [
				{name: 'ratingValue', label: 'Điểm trung bình', type: 'number'},
				{name: 'reviewCount', label: 'Số lượt đánh giá', type: 'number'},
			]},
		],
	},

	FAQPage: {
		label: 'Trang hỏi đáp (FAQPage)',
		fields: [
			{name: 'mainEntity', label: 'Câu hỏi', type: 'array', of: 'Question', required: true, rich: true, fields: [
				{name: 'name', label: 'Câu hỏi', type: 'text'},
				{name: 'acceptedAnswer', label: 'Câu trả lời', type: 'object', of: 'Answer', fields: [
					{name: 'text', label: 'Nội dung trả lời', type: 'textarea'},
				]},
			]},
		],
	},

	LocalBusiness: {
		label: 'Doanh nghiệp địa phương (LocalBusiness)',
		fields: [
			{name: 'name', label: 'Tên doanh nghiệp', type: 'text', required: true, rich: true},
			{name: 'image', label: 'URL ảnh', type: 'url', rich: true},
			{name: 'telephone', label: 'Số điện thoại', type: 'tel'},
			{name: 'priceRange', label: 'Khoảng giá (vd $$)', type: 'text'},
			{name: 'url', label: 'Website', type: 'url'},
			{name: 'address', label: 'Địa chỉ', type: 'object', of: 'PostalAddress', required: true, rich: true, fields: ADDRESS_FIELDS},
			{name: 'geo', label: 'Toạ độ', type: 'object', of: 'GeoCoordinates', fields: [
				{name: 'latitude', label: 'Vĩ độ', type: 'text'},
				{name: 'longitude', label: 'Kinh độ', type: 'text'},
			]},
			{name: 'openingHoursSpecification', label: 'Giờ mở cửa', type: 'array', of: 'OpeningHoursSpecification', fields: [
				{name: 'dayOfWeek', label: 'Ngày (vd Monday)', type: 'text'},
				{name: 'opens', label: 'Giờ mở (HH:MM)', type: 'text'},
				{name: 'closes', label: 'Giờ đóng (HH:MM)', type: 'text'},
			]},
		],
	},

	Organization: {
		label: 'Tổ chức (Organization)',
		fields: [
			{name: 'name', label: 'Tên tổ chức', type: 'text', required: true, rich: true},
			{name: 'url', label: 'Website', type: 'url', rich: true},
			{name: 'logo', label: 'URL logo', type: 'url', rich: true},
			{name: 'sameAs', label: 'Trang mạng xã hội (mỗi URL 1 dòng)', type: 'textarea'},
			{name: 'contactPoint', label: 'Liên hệ', type: 'object', of: 'ContactPoint', fields: [
				{name: 'telephone', label: 'Số điện thoại', type: 'tel'},
				{name: 'contactType', label: 'Loại (vd customer service)', type: 'text'},
			]},
		],
	},

	BreadcrumbList: {
		label: 'Đường dẫn (BreadcrumbList)',
		fields: [
			{name: 'itemListElement', label: 'Các cấp', type: 'array', of: 'ListItem', required: true, rich: true, fields: [
				{name: 'position', label: 'Thứ tự (1,2,3…)', type: 'number'},
				{name: 'name', label: 'Tên', type: 'text'},
				{name: 'item', label: 'URL', type: 'url'},
			]},
		],
	},

	Event: {
		label: 'Sự kiện (Event)',
		fields: [
			{name: 'name', label: 'Tên sự kiện', type: 'text', required: true, rich: true},
			{name: 'startDate', label: 'Bắt đầu', type: 'datetime', required: true, rich: true},
			{name: 'endDate', label: 'Kết thúc', type: 'datetime'},
			{name: 'image', label: 'URL ảnh', type: 'url'},
			{name: 'description', label: 'Mô tả', type: 'textarea'},
			{name: 'location', label: 'Địa điểm', type: 'object', of: 'Place', required: true, rich: true, fields: [
				{name: 'name', label: 'Tên địa điểm', type: 'text'},
				{name: 'address', label: 'Địa chỉ', type: 'object', of: 'PostalAddress', fields: ADDRESS_FIELDS},
			]},
			{name: 'offers', label: 'Vé (Offer)', type: 'object', of: 'Offer', fields: [
				{name: 'price', label: 'Giá', type: 'number'},
				{name: 'priceCurrency', label: 'Tiền tệ', type: 'text'},
				{name: 'url', label: 'URL mua vé', type: 'url'},
			]},
		],
	},
};

/** Một số property là danh sách URL nhập theo dòng → tách thành mảng khi build. */
const MULTILINE_ARRAY_FIELDS = ['sameAs'];

/**
 * Dựng 1 entity (đệ quy) — bỏ qua field rỗng; trả null nếu không có giá trị nào.
 */
function buildEntity(typeName, fields, vals) {
	const out = {'@type': typeName};
	let has = false;

	for (const f of fields) {
		const v = vals?.[f.name];

		if (f.type === 'object') {
			const child = buildEntity(f.of, f.fields, v || {});
			if (child) { out[f.name] = child; has = true; }
		} else if (f.type === 'array') {
			const arr = (Array.isArray(v) ? v : [])
				.map((item) => buildEntity(f.of, f.fields, item || {}))
				.filter(Boolean);
			if (arr.length) { out[f.name] = arr; has = true; }
		} else {
			if (v !== undefined && v !== null && String(v).trim() !== '') {
				if (MULTILINE_ARRAY_FIELDS.includes(f.name)) {
					const list = String(v).split('\n').map((s) => s.trim()).filter(Boolean);
					if (list.length) { out[f.name] = list.length === 1 ? list[0] : list; has = true; }
				} else {
					out[f.name] = f.type === 'number' ? Number(v) : v;
					has = true;
				}
			}
		}
	}

	return has ? out : null;
}

/**
 * Sinh JSON-LD chuẩn từ type + giá trị form.
 * @returns {object} { '@context':'https://schema.org', '@type':type, ... }
 */
export function buildJsonLd(type, values) {
	const def = SCHEMA_TYPES[type];

	if (!def) return {'@context': 'https://schema.org', '@type': type};

	const entity = buildEntity(type, def.fields, values || {}) || {'@type': type};

	return {'@context': 'https://schema.org', ...entity};
}

/** Ngược với buildEntity: đọc JSON-LD → values của form (đệ quy theo định nghĩa field). */
function parseEntity(fields, obj) {
	const vals = {};

	if (!obj || typeof obj !== 'object') return vals;

	for (const f of fields) {
		const v = obj[f.name];
		if (v === undefined || v === null) continue;

		if (f.type === 'object') {
			vals[f.name] = parseEntity(f.fields, v);
		} else if (f.type === 'array') {
			vals[f.name] = (Array.isArray(v) ? v : [v]).map((item) => parseEntity(f.fields, item));
		} else if (MULTILINE_ARRAY_FIELDS.includes(f.name)) {
			vals[f.name] = (Array.isArray(v) ? v : [v]).join('\n');
		} else {
			vals[f.name] = v;
		}
	}

	return vals;
}

/**
 * JSON-LD đã lưu → giá trị form của Generator (nút "Sửa" ở danh sách đã lưu).
 * Chỉ giữ các property có trong định nghĩa; property lạ bị bỏ qua.
 */
export function parseJsonLdToValues(type, json) {
	const def = SCHEMA_TYPES[type];

	if (!def || !json || typeof json !== 'object') return {};

	return parseEntity(def.fields, json);
}

/** Dữ liệu mẫu theo loại — nút "Điền dữ liệu mẫu" để user thấy form hoàn chỉnh trông ra sao. */
export const SAMPLE_VALUES = {
	Article: {
		headline: '10 mẹo tối ưu SEO on-page cho người mới',
		image: 'https://example.com/images/seo-onpage.jpg',
		author: {name: 'Nguyễn Văn An', url: 'https://example.com/author/an'},
		publisher: {name: 'Omni SEO Blog', logo: 'https://example.com/logo.png'},
		datePublished: '2026-06-01T09:00',
		dateModified: '2026-06-15T10:30',
		description: 'Hướng dẫn từng bước tối ưu tiêu đề, meta, heading và liên kết nội bộ.',
	},
	Product: {
		name: 'Giày chạy bộ UltraLight 2',
		image: 'https://example.com/images/ultralight-2.jpg',
		description: 'Giày chạy bộ siêu nhẹ 180g, đệm phản hồi cao, phù hợp chạy đường dài.',
		sku: 'UL2-BLK-42',
		brand: {name: 'FastFeet'},
		offers: {price: 1590000, priceCurrency: 'VND', availability: 'https://schema.org/InStock', url: 'https://example.com/giay-ultralight-2'},
		aggregateRating: {ratingValue: 4.7, reviewCount: 128},
	},
	FAQPage: {
		mainEntity: [
			{name: 'Thời gian giao hàng mất bao lâu?', acceptedAnswer: {text: 'Đơn nội thành giao trong 24h, các tỉnh khác 2–4 ngày làm việc.'}},
			{name: 'Có được đổi trả không?', acceptedAnswer: {text: 'Bạn được đổi trả miễn phí trong 30 ngày nếu sản phẩm còn nguyên tem.'}},
		],
	},
	LocalBusiness: {
		name: 'Cà phê Sáng Sớm',
		image: 'https://example.com/images/cafe.jpg',
		telephone: '+84901234567',
		priceRange: '$$',
		url: 'https://example.com',
		address: {streetAddress: '123 Lê Lợi', addressLocality: 'Phường Bến Thành', addressRegion: 'TP. Hồ Chí Minh', postalCode: '700000', addressCountry: 'VN'},
		geo: {latitude: '10.7769', longitude: '106.7009'},
		openingHoursSpecification: [
			{dayOfWeek: 'Monday', opens: '07:00', closes: '22:00'},
		],
	},
	Organization: {
		name: 'Công ty TNHH Omni SEO',
		url: 'https://example.com',
		logo: 'https://example.com/logo.png',
		sameAs: 'https://facebook.com/omniseo\nhttps://x.com/omniseo',
		contactPoint: {telephone: '+84281234567', contactType: 'customer service'},
	},
	BreadcrumbList: {
		itemListElement: [
			{position: 1, name: 'Trang chủ', item: 'https://example.com'},
			{position: 2, name: 'Blog', item: 'https://example.com/blog'},
			{position: 3, name: 'Bài viết SEO', item: 'https://example.com/blog/bai-viet-seo'},
		],
	},
	Event: {
		name: 'Workshop SEO 2026',
		startDate: '2026-08-20T08:30',
		endDate: '2026-08-20T17:00',
		image: 'https://example.com/images/workshop.jpg',
		description: 'Workshop 1 ngày về SEO kỹ thuật và content cho doanh nghiệp nhỏ.',
		location: {name: 'Trung tâm Hội nghị ABC', address: {streetAddress: '456 Nguyễn Huệ', addressLocality: 'Quận 1', addressRegion: 'TP. Hồ Chí Minh', addressCountry: 'VN'}},
		offers: {price: 500000, priceCurrency: 'VND', url: 'https://example.com/dang-ky-workshop'},
	},
};
